import { updateEmailResendCount, updatePhoneResendTime } from '@/commonFunctions/utils'
import { ButtonPrimary } from '@/components/button'

import { LOCAL_EMAIL_RESEND_COUNT, LOCAL_EMAIL_RESEND_END_TIME } from '@/constants/Common'
import { APP_CUSTOM_ERROR, VERIFY_EMAIL_ERROR } from '@/constants/MessageConstants'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { userSelector } from '@/pages/user/selectors'
import { getProfile, setVerifyEmailModalState } from '@/pages/user/userSlice'
import { useVerifyEmail } from '@/services'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LogoutModal } from './LogoutModal'

type Props = {
  open: boolean
}

export const VerifyEmailModal = ({ open }: Props) => {
  const dispatch = useAppDispatch()
  const { user, verifyEmailModalState, isLoading } = useAppSelector(userSelector)

  const { mutate: verifyEmail } = useVerifyEmail()

  const emailResendCount = user ? JSON.parse(localStorage.getItem(LOCAL_EMAIL_RESEND_COUNT) as any) : null
  const EMAIL_END_TIME = user ? JSON.parse(localStorage.getItem(LOCAL_EMAIL_RESEND_END_TIME) as any) : null
  const dif = updatePhoneResendTime(EMAIL_END_TIME)
  const [resendEmail, setResendEmail] = useState(dif > 0)
  const [email, setEmail] = useState('')
  const [isEdit, setIsEdit] = useState(false)
  const [emailSec, setEmailSec] = useState(0)
  const [emailMin, setEmailMin] = useState(0)
  const [sentEmail, setSentEmail] = useState(false)
  const [emailResendTime, setEmailResendTime] = useState(dif > 0 ? Math.round(dif) : 0)
  const [emailError, setEmailError] = useState('')
  const [verifyNowError, setVerifyNowError] = useState('')
  const [verifyNow, setVerifyNow] = useState(false)
  const [logoutModal, setLogoutModal] = useState(false)

  const toggleLogoutModal = () => {
    setLogoutModal(!logoutModal)
  }

  const { t } = useTranslation()

  const onCloseModal = () => {
    // onClose()
    setEmail(user?.email as string)
    setVerifyNow(false)
  }

  const handleVerifyNow = () => {
    setVerifyNow(true)
    setEmailError('')
    dispatch(getProfile())
  }

  const resendEmailClick = () => {
    setSentEmail(true)
    setVerifyNowError('')
    setVerifyNow(false)
    verifyEmail(
      { email },
      {
        onSuccess: () => {
          setSentEmail(false)
          setResendEmail(true)
          updateEmailResendCount()
          let RESEND_TIME = 29
          let END_TIME = 29
          if (emailResendCount === 0) {
            END_TIME = new Date().getTime() + 1000 * RESEND_TIME
            localStorage.setItem(LOCAL_EMAIL_RESEND_END_TIME, END_TIME.toString())
            setEmailResendTime(RESEND_TIME)
            setEmailMin(Math.floor(RESEND_TIME / 60))
            setEmailSec(RESEND_TIME % 60)
          } else if (emailResendCount === 1) {
            RESEND_TIME = 59
            END_TIME = new Date().getTime() + 1000 * RESEND_TIME
            localStorage.setItem(LOCAL_EMAIL_RESEND_END_TIME, END_TIME.toString())
            setEmailResendTime(RESEND_TIME)
            setEmailMin(Math.floor(RESEND_TIME / 60))
            setEmailSec(RESEND_TIME % 60)
          } else {
            RESEND_TIME = 299
            END_TIME = new Date().getTime() + 1000 * RESEND_TIME
            localStorage.setItem(LOCAL_EMAIL_RESEND_END_TIME, END_TIME.toString())
            setEmailResendTime(RESEND_TIME)
            setEmailMin(Math.floor(RESEND_TIME / 60))
            setEmailSec(RESEND_TIME % 60)
          }
          setResendEmail(true)
          if (email !== user?.email) {
            dispatch(setVerifyEmailModalState(!verifyEmailModalState))
            setLogoutModal(true)
          }
        },
        onError: () => {
          setSentEmail(false)
          setEmailError(APP_CUSTOM_ERROR)
        },
      }
    )
  }

  useEffect(() => {
    const emailId = setInterval(() => {
      if (emailResendTime > 0) {
        const emaildif = updatePhoneResendTime(EMAIL_END_TIME)
        setEmailResendTime(Math.round(emaildif - 1))
        setEmailMin(Math.floor(emailResendTime / 60))
        setEmailSec(emailResendTime % 60)
      } else {
        localStorage.setItem(LOCAL_EMAIL_RESEND_END_TIME, '0')
      }
    }, 1000)
    if (emailResendTime === 0) {
      setResendEmail(false)
    }
    return () => {
      clearInterval(emailId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailResendTime])

  useEffect(() => {
    if (user) setEmail(user.email)
  }, [user])

  useEffect(() => {
    if (user?.isValidEmail && verifyNow) {
      window.location.reload()
    }
    if (!user?.isValidEmail && verifyNow && !isLoading) {
      setVerifyNowError(VERIFY_EMAIL_ERROR)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyNow, isLoading])

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col max-w-2xl transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                {/* <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={onCloseModal}
                >
                  <Close className="text-navy-900" />
                </button> */}

                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('common.verifyEmail')}
                </Dialog.Title>

                <div className="flex flex-col py-4 gap-y-4">
                  <p className="text-navy-900 text-sm font-semibold text-center">{t('common.mustVerify')}</p>
                  <p className="text-gray-400 font-semibold text-center">{t('common.clickResendLink')}</p>

                  {emailError && (
                    <div className="flex justify-center">
                      {emailError ? (
                        <p className="text-sm text-boldOrange font-medium text-center">{emailError}</p>
                      ) : verifyNowError && verifyNow ? (
                        <p className="text-sm text-boldOrange font-medium text-center">{verifyNowError}</p>
                      ) : (
                        <></>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col items-center gap-y-8">
                    <input
                      className={`py-2 px-4 border rounded text-sm text-navyBlue focus:outline-none ${
                        !isEdit && 'font-semibold bg-primary-500 bg-opacity-20'
                      }`}
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={!isEdit}
                    />
                    <span className="text-xs text-gray-500">
                      {t('common.changeEmail')}
                      <span
                        className="text-primary-500 px-4 font-medium hover:bg-primary-500 hover:bg-opacity-10 py-2 rounded hover:cursor-pointer"
                        onClick={() => setIsEdit(true)}
                      >
                        {t('common.edit')}
                      </span>
                    </span>
                  </div>
                </div>

                {resendEmail ? (
                  <p className="text-green-500 text-xs text-center mb-4">{t('common.aLinkWasSent')}</p>
                ) : null}

                <div className="flex  justify-center gap-x-4 pt-2">
                  <button
                    className="py-[6px] px-4 rounded font-semibold text-sm text-navy-900 border shadow-lg hover:bg-slate-100"
                    onClick={handleVerifyNow}
                  >
                    {t('common.alreadyVerified')}
                  </button>
                  <ButtonPrimary
                    title={
                      resendEmail
                        ? emailSec < 10
                          ? `0${emailMin}:0${emailSec}`
                          : `0${emailMin}:${emailSec}`
                        : t('common.resendLink')
                    }
                    className={`!py-[6px] !px-4 !rounded`}
                    disabled={resendEmail || !email || sentEmail}
                    onClick={resendEmailClick}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <LogoutModal open={logoutModal} onClose={toggleLogoutModal} />
      </Dialog>
    </Transition>
  )
}
